<template>
  <div>
    <!-- "Deploy new app": the catalog of ready-made apps + a custom compose file. -->
    <template v-if="view === 'new'">
      <div class="d-flex align-center mb-2">
        <gg-btn small text class="kvm-dk-text ml-n2" @click="back">
          <v-icon left small aria-hidden="true">mdi-arrow-left</v-icon>{{ backLabel }}
        </gg-btn>
      </div>
      <h3 class="body-1 font-weight-medium mb-1">Deploy a new app</h3>
      <kvm-docker-templates :service-id="serviceId" :refresh-key="refreshKey"
                            @operation="$emit('operation', $event)" @done="back" @custom="openNew" />
    </template>

    <!-- One app's Manage page. -->
    <kvm-docker-app-manage v-else-if="view === 'manage' && managedStack" :service-id="serviceId" :stack="managedStack"
                           :busy-label="busy[managedStack.name] ? busy[managedStack.name].label : ''" :refresh-key="refreshKey"
                           @back="back" @action="action(managedStack, $event)"
                           @edit="openEdit(managedStack)" @remove="confirmRemove(managedStack)" />
    <div v-else-if="view === 'manage' && loading" class="py-6 text-center" role="status">
      <v-progress-circular indeterminate color="primary" size="32" width="3" />
      <span class="kvm-sr-only">Loading the app…</span>
    </div>

    <template v-else>
    <div class="d-flex flex-wrap align-center mb-3">
      <span class="kvm-help text-caption mr-auto">The apps installed on this server.</span>
      <gg-btn small tile outlined color="primary" class="mr-2 mb-1" :loading="loading" @click="load">
        <v-icon left small aria-hidden="true">mdi-refresh</v-icon>Refresh
      </gg-btn>
      <gg-btn small tile color="primary" class="mb-1 kvm-dk-primary" @click="view = 'new'">
        <v-icon left small aria-hidden="true">mdi-plus</v-icon>Deploy new app
      </gg-btn>
    </div>

    <v-alert v-if="error" type="error" border="left" dense role="alert">{{ error }}</v-alert>

    <div v-if="loading && !loaded" class="py-6 text-center" role="status">
      <v-progress-circular indeterminate color="primary" size="32" width="3" />
      <span class="kvm-sr-only">Loading apps…</span>
    </div>
    <div v-else-if="loaded && !stacks.length" class="my-6 text-center">
      <p class="kvm-help mb-3">No apps yet.</p>
      <gg-btn tile color="primary" class="kvm-dk-primary" @click="view = 'new'">
        <v-icon left small aria-hidden="true">mdi-plus</v-icon>Deploy your first app
      </gg-btn>
    </div>

    <v-simple-table v-else-if="stacks.length" dense class="kvm-dk-table">
      <caption class="kvm-sr-only">Apps</caption>
      <thead>
        <tr>
          <th scope="col">Name</th>
          <th scope="col">Status</th>
          <th scope="col" class="text-right"><span class="kvm-sr-only">Actions</span></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="s in stacks" :key="s.name" :class="{ 'kvm-dk-row--busy': busy[s.name] }" :aria-busy="busy[s.name] ? 'true' : 'false'">
          <td data-label="Name">
            <span class="font-weight-medium">{{ s.name }}</span>
            <v-chip v-if="s.system" x-small label class="ml-2" title="Runs your domains — managed from the Domains tab">domains</v-chip>
            <v-chip v-else-if="!s.managed" x-small label class="ml-2" title="Created outside this panel — view only">external</v-chip>
          </td>
          <td data-label="Status">
            <span v-if="busy[s.name]" class="kvm-dk-busy" role="status">
              <v-progress-circular indeterminate size="14" width="2" color="primary" class="mr-1" aria-hidden="true" />{{ busy[s.name].label }}
            </span>
            <template v-else>{{ s.status }}</template>
          </td>
          <td data-label="Actions" class="text-right text-no-wrap">
            <gg-btn v-if="!s.system" small tile outlined color="primary" class="mr-1" :disabled="!!busy[s.name]" @click="openManage(s)">Manage</gg-btn>
            <v-menu offset-y left>
              <template #activator="{ on, attrs }">
                <gg-btn icon small :aria-label="'Actions for ' + s.name" :disabled="!!busy[s.name]" v-bind="attrs" v-on="on"><v-icon small>mdi-dots-vertical</v-icon></gg-btn>
              </template>
              <v-list dense>
                <v-list-item v-if="!s.system" @click="openEdit(s)"><v-list-item-title>{{ s.managed ? 'Edit compose file' : 'View details' }}</v-list-item-title></v-list-item>
                <v-list-item v-if="s.managed && !s.system" @click="action(s, 'up')"><v-list-item-title>Redeploy</v-list-item-title></v-list-item>
                <v-list-item v-if="s.managed && !s.system" @click="action(s, 'update')"><v-list-item-title>Update to the newest build</v-list-item-title></v-list-item>
                <v-list-item @click="action(s, 'restart')"><v-list-item-title>Restart</v-list-item-title></v-list-item>
                <v-list-item v-if="!s.system" @click="action(s, 'stop')"><v-list-item-title>Stop</v-list-item-title></v-list-item>
                <v-list-item @click="action(s, 'start')"><v-list-item-title>Start</v-list-item-title></v-list-item>
                <v-list-item v-if="!s.system" @click="action(s, 'down')"><v-list-item-title>Take down (remove containers)</v-list-item-title></v-list-item>
                <v-list-item v-if="s.managed && !s.system" @click="confirmRemove(s)"><v-list-item-title class="error--text">Delete app…</v-list-item-title></v-list-item>
              </v-list>
            </v-menu>
          </td>
        </tr>
      </tbody>
    </v-simple-table>
    </template>

    <!-- Editor (new / edit / view) -->
    <v-dialog v-model="editor.open" max-width="900" persistent>
      <gg-card role="dialog" aria-modal="true" aria-labelledby="kvm-dk-stack-title">
        <gg-card-text class="pa-4">
          <h3 id="kvm-dk-stack-title" class="body-1 font-weight-medium mb-3">
            {{ editor.isNew ? 'Custom app' : (editor.readonly ? 'App' : 'Edit app') }}{{ editor.isNew ? '' : ` — ${editor.name}` }}
          </h3>
          <div v-if="editor.loading" class="py-6 text-center"><v-progress-circular indeterminate color="primary" /></div>
          <template v-else>
            <v-text-field v-if="editor.isNew" v-model.trim="editor.name" label="App name" outlined dense
                          hint="Lowercase letters, numbers, - and _ (e.g. my-app)" persistent-hint class="mb-3" />
            <v-alert v-if="editor.readonly" type="info" dense text class="mb-3">
              This app was created outside this panel, so its compose file isn't editable here. You can still restart, stop, start or take it down.
            </v-alert>
            <v-textarea v-if="!editor.readonly" v-model="editor.compose" label="docker-compose.yml" outlined rows="16"
                        class="kvm-dk-code" spellcheck="false" autocomplete="off" :placeholder="composePlaceholder" />
            <template v-if="editor.containers.length">
              <div class="subtitle-2 mb-1">Containers</div>
              <v-simple-table dense class="mb-2">
                <tbody>
                  <tr v-for="c in editor.containers" :key="c.name">
                    <td class="font-weight-medium">{{ c.service }}</td>
                    <td class="kvm-dk-mono">{{ c.image }}</td>
                    <td>{{ c.status }}</td>
                    <td class="kvm-dk-mono text-caption">{{ c.ports || '—' }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </template>
            <v-alert v-if="editor.error" type="warning" dense text class="mb-0" role="alert" style="white-space: pre-wrap;">{{ editor.error }}</v-alert>
          </template>
          <div class="text-right mt-3">
            <gg-btn text class="mr-2" :disabled="editor.saving" @click="editor.open = false">{{ editor.readonly ? 'Close' : 'Cancel' }}</gg-btn>
            <gg-btn v-if="!editor.readonly" tile color="primary" class="kvm-dk-primary" :loading="editor.saving"
                    :disabled="!editor.name || !editor.compose.trim()" @click="save">
              Save &amp; deploy
            </gg-btn>
          </div>
        </gg-card-text>
      </gg-card>
    </v-dialog>

    <!-- Delete confirm -->
    <v-dialog v-model="remove.open" max-width="460">
      <gg-card v-if="remove.s" role="dialog" aria-modal="true" aria-labelledby="kvm-dk-srm-title">
        <gg-card-text class="pa-4">
          <h3 id="kvm-dk-srm-title" class="body-1 font-weight-medium mb-2">Delete app “{{ remove.s.name }}”?</h3>
          <div v-if="remove.loading" class="py-4 text-center" role="status">
            <v-progress-circular indeterminate color="primary" size="24" width="2" />
            <span class="kvm-sr-only">Checking what the app contains…</span>
          </div>
          <template v-else>
            <p class="mb-2">This removes:</p>
            <ul class="kvm-dk-rm mb-3">
              <li v-for="c in remove.containers" :key="c.name">
                Container <strong>{{ c.service || c.name }}</strong> <span class="kvm-help kvm-dk-mono">{{ c.image }}</span>
              </li>
              <li v-for="r in remove.routes" :key="r.hostname + r.path">
                Domain <strong>{{ r.hostname }}{{ r.path === '/' ? '' : r.path }}</strong>
                <span class="kvm-help">— it stops working and is free to use again</span>
              </li>
              <li>Its compose file</li>
            </ul>
            <p v-if="remove.routes.length" class="kvm-help text-caption mb-3">
              DNS records for your own domains stay as they are — remove them in that domain's DNS if you no longer need them.
            </p>
            <template v-if="remove.volumes_list.length">
              <v-checkbox v-model="remove.volumes" dense hide-details class="mt-0"
                          :label="`Also delete its data (${remove.volumes_list.length === 1 ? '1 volume' : remove.volumes_list.length + ' volumes'})`" />
              <p class="kvm-help text-caption ml-8 mb-3">
                <span class="kvm-dk-mono">{{ remove.volumes_list.join(', ') }}</span><br>
                {{ remove.volumes ? 'The app\'s data is permanently erased.' : 'Kept — deploying an app with the same name again reuses it.' }}
              </p>
            </template>
            <v-alert v-if="remove.error" type="warning" dense text class="mb-3">{{ remove.error }}</v-alert>
          </template>
          <div class="text-right">
            <gg-btn text class="mr-2" @click="remove.open = false">Cancel</gg-btn>
            <gg-btn tile color="error" :disabled="remove.loading" @click="doRemove">Delete</gg-btn>
          </div>
        </gg-card-text>
      </gg-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import kvm from '@/api/kvm'
import KvmDockerAppManage from '@/views/services/hosting/kvm/KvmDockerAppManage'
import KvmDockerTemplates from '@/views/services/hosting/kvm/KvmDockerTemplates'

const STACK_NAME = /^[a-z0-9][a-z0-9_-]{0,62}$/

const BUSY_LABELS = {
  up: 'Deploying…',
  pull: 'Pulling images…',
  restart: 'Restarting…',
  stop: 'Stopping…',
  start: 'Starting…',
  down: 'Taking down…',
  remove: 'Deleting…',
  update: 'Updating…',
}

/**
 * One server's apps: the list, "Deploy new app" (templates + custom) and each
 * app's Manage page. A server's Docker tab shows the list; the Applications pages
 * open it straight at `start` = 'new' (deploy to this server) or 'manage' (the
 * `app` named) — leaving that first view (Back, Done, or the app being deleted)
 * then emits `exit` for the page to navigate instead of showing the list.
 */
export default {
  name: 'KvmDockerStacks',
  components: { KvmDockerAppManage, KvmDockerTemplates },
  props: {
    serviceId: { type: [String, Number], required: true },
    refreshKey: { type: Number, default: 0 },
    // Ids of finished operations (from the parent), to clear a row's busy state.
    finishedOps: { type: Array, default: () => [] },
    start: { type: String, default: 'list', validator: v => ['list', 'new', 'manage'].includes(v) },
    app: { type: String, default: '' },
  },
  data () {
    return {
      stacks: [],
      loading: false,
      loaded: false,
      error: '',
      editor: { open: false, isNew: false, readonly: false, loading: false, saving: false, name: '', compose: '', containers: [], error: '' },
      remove: { open: false, loading: false, s: null, volumes: false, containers: [], routes: [], volumes_list: [], error: '' },
      // stack name → { label, opId } while an action on it is running
      busy: {},
      // 'list' = installed apps; 'new' = pick what to deploy; 'manage' = one app
      view: this.start,
      manageName: this.app,
      composePlaceholder: 'services:\n  web:\n    image: nginx:stable\n    ports:\n      - "8080:80"\n    restart: unless-stopped\n',
    }
  },
  computed: {
    managedStack () {
      return this.stacks.find(s => s.name === this.manageName) || null
    },
    backLabel () {
      return this.start === 'new' ? 'Back' : 'Back to apps'
    },
  },
  watch: {
    refreshKey () { this.load() },
    finishedOps (ids) {
      for (const [name, b] of Object.entries(this.busy)) {
        if (b.opId && ids.includes(b.opId)) this.$delete(this.busy, name)
      }
    },
  },
  methods: {
    ...mapActions(['reportError']),
    async load () {
      this.loading = true
      try {
        this.stacks = await kvm.dockerStacks(this.serviceId)
        this.error = ''
        // The app being managed was deleted (or isn't there) → back to the list.
        if (this.view === 'manage' && !this.managedStack) this.back()
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
        this.loaded = true
      }
    },
    openManage (s) {
      this.manageName = s.name
      this.view = 'manage'
    },
    // Leave the current view: to the list, or out to the page that opened us there.
    back () {
      if (this.start !== 'list' && this.view === this.start) {
        this.$emit('exit')
        return
      }
      this.view = 'list'
    },
    openNew () {
      this.editor = { open: true, isNew: true, readonly: false, loading: false, saving: false, name: '', compose: '', containers: [], error: '' }
    },
    async openEdit (s) {
      this.editor = { open: true, isNew: false, readonly: !s.managed, loading: true, saving: false, name: s.name, compose: '', containers: [], error: '' }
      try {
        const st = await kvm.dockerStack(this.serviceId, s.name)
        this.editor.compose = st.compose || ''
        this.editor.containers = st.containers || []
        this.editor.readonly = st.compose == null
      } catch (e) {
        this.editor.error = e.message
      } finally {
        this.editor.loading = false
      }
    },
    async save () {
      const ed = this.editor
      if (!STACK_NAME.test(ed.name)) {
        ed.error = 'Use lowercase letters, numbers, - and _ for the app name (start with a letter or number).'
        return
      }
      ed.saving = true
      ed.error = ''
      try {
        const op = await kvm.dockerSaveStack(this.serviceId, ed.name, ed.compose)
        this.$emit('operation', op)
        ed.open = false
        if (ed.isNew) this.back() // a new custom app → wherever "Deploy new app" returns to
      } catch (e) {
        ed.error = e.message
      } finally {
        ed.saving = false
      }
    },
    // Grey the row out with what's happening until its operation finishes.
    markBusy (s, action, op) {
      this.$set(this.busy, s.name, { label: BUSY_LABELS[action] || 'Working…', opId: op && op.id })
    },
    async action (s, action) {
      this.markBusy(s, action)
      try {
        const op = await kvm.dockerStackAction(this.serviceId, s.name, action)
        this.markBusy(s, action, op)
        this.$emit('operation', op)
      } catch (e) {
        this.$delete(this.busy, s.name)
        this.reportError(e)
      }
    },
    async confirmRemove (s) {
      this.remove = { open: true, loading: true, s, volumes: false, containers: [], routes: [], volumes_list: [], error: '' }
      try {
        const st = await kvm.dockerStack(this.serviceId, s.name)
        this.remove.containers = st.containers || []
        this.remove.routes = st.routes || []
        this.remove.volumes_list = st.volumes || []
      } catch (e) {
        this.remove.error = `Couldn't check what's in the app (${e.message}). Deleting still removes all of its containers and domains.`
      } finally {
        this.remove.loading = false
      }
    },
    async doRemove () {
      const { s, volumes } = this.remove
      this.remove.open = false
      this.markBusy(s, 'remove')
      try {
        const op = await kvm.dockerRemoveStack(this.serviceId, s.name, volumes)
        this.markBusy(s, 'remove', op)
        this.$emit('operation', op)
      } catch (e) {
        this.$delete(this.busy, s.name)
        this.reportError(e)
      }
    },
  },
  mounted () {
    this.load()
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

.kvm-sr-only { @include kvm-sr-only; }
.kvm-help { @include kvm-secondary-text; }
.kvm-dk-mono { font-family: monospace; font-size: 0.8rem; }
.kvm-dk-primary { @include kvm-btn-primary-aa; }
.kvm-dk-text { @include kvm-text-green-aa; }
.kvm-dk-row--busy td:not([data-label="Status"]) { opacity: 0.5; }
.kvm-dk-busy { display: inline-flex; align-items: center; font-style: italic; }
.kvm-dk-rm { padding-left: 1.2rem; li { margin-bottom: 2px; } }
.kvm-dk-table {
  @include kvm-stacked-table;
  th { white-space: nowrap; }
}
.kvm-dk-code ::v-deep textarea {
  font-family: monospace;
  font-size: 0.82rem;
  line-height: 1.4;
}
::v-deep .v-btn:focus-visible { @include kvm-focus-ring; }
</style>
